import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaRegFilePdf } from "react-icons/fa6";
import { TbFileExcel } from "react-icons/tb";
import { FaRegCopy } from "react-icons/fa";
import { BsFiletypeCsv } from "react-icons/bs";
import Batteryicon from "../../assets/BatteryIcon.png";
import Solaricon from "../../assets/SolarIcon.png";
import Windicon from "../../assets/WindIcon.png";
import Gasicon from "../../assets/GasIcon.png";
import Coalicon from "../../assets/Coalicon.png";
import Hydroicon from "../../assets/Hydroicon.png";
import Repicon from "../../assets/Repicon.png";
import Transmissionicon from "../../assets/Transmissionicon.png";
import BatteryLevel from "../../components/Charts/Battery/BatteryLevel";
import { GiElectric } from "react-icons/gi";
import { IoMdArrowRoundDown } from "react-icons/io";
import { BiSolidDollarCircle } from "react-icons/bi";
import { LuFlagTriangleRight } from "react-icons/lu";
// import { HiSun } from "react-icons/hi";
import { FaWind } from "react-icons/fa";
import { ImFire } from "react-icons/im";
import "./LineViewTable.css";
import { useDataContext } from "../DataContext/PortfolioDataContext";
// import axios from 'axios';
import LoadingSpinner from "../Loading/Loading";
import {
  exportTableToPDF,
  useExportLogo,
} from "../../components/AssetDashboard/ConfigCharts/ExcelCurved";
import { exportTableOldToExcel } from "../../components/AssetDashboard/ConfigCharts/ExcelCurved";
import { exportToCSV } from "../../components/AssetDashboard/ConfigCharts/ExportUtils";
import { useEntitlements } from "../DataContext/EntitlementsContext";
import { slugify } from "../AssetDashboard/slugify";
import { tabsConfig } from "../AssetDashboard/tabsConfig";

type SortConfig = {
  key: string;
  direction: "asc" | "desc";
};

type TableCardProps = {
  selectedAssetType: string;
  selectedISO: string | null;
  sortBy: string | null;
  sortOrder: string;
  groupBy: string | null;
  searchQuery: string;
};

const TableCard: React.FC<TableCardProps> = ({
  selectedAssetType,
  selectedISO,
  sortBy,
  sortOrder,
  groupBy,
  searchQuery,
}) => {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "",
    direction: "asc",
  });
  const { assetData, error } = useDataContext();
  const [noResults, setNoResults] = useState(false);
  const { logo } = useExportLogo();
  const [selectedRows, setSelectedRows] = React.useState<any[]>([]);
  const { entitlements } = useEntitlements();
  const hasAccess = (path: string) =>
    entitlements.some((e) => e.po_path === path);

  useEffect(() => {
    if (assetData) {
      setTableData(assetData);
      setLoading(false);
    }
  }, [assetData]);

  const handleCardClick = (asset: any) => {
    if (!hasAccess("/asset-dashboard")) return;

    const slug = slugify(asset.asset_name);

    const entitledPaths = entitlements.map((e) =>
      e.po_path.toLowerCase().trim()
    );

    const accessibleTab = tabsConfig.tabs.find((tab) => {
      const tabSlug = tab.label.toLowerCase().replace(/\s+/g, "-");
      const fullPath = `/asset-dashboard/${tabSlug}`;
      return entitledPaths.includes(fullPath);
    });

    if (!accessibleTab) {
      console.warn("No accessible tab found for user.");
      return;
    }

    const tabSlug = accessibleTab.label.toLowerCase().replace(/\s+/g, "-");

    navigate(`/asset-dashboard/${tabSlug}/${slug}`, {
      state: {
        ASSET_ID: asset.asset_id,
        ASSET_NAME: asset.asset_name,
        ASSET_TYPE: asset.asset_type,
        ISO_CODE: asset.ISO,
      },
    });
  };

  const getTypeIcon = (assetType: string | null) => {
    switch (assetType) {
      case "Battery":
        return Batteryicon;
      case "Solar":
        return Solaricon;
      case "Wind":
        return Windicon;
      case "Gas":
        return Gasicon;
      case "Hydro":
        return Hydroicon;
      case "Rep":
        return Repicon;
      case "Coal":
        return Coalicon;
      case "Transmission":
        return Transmissionicon;
      default:
        return null;
    }
  };

  const sortData = (key: string) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({ key, direction });

    const sortedData = [...tableData].sort((a, b) => {
      let aValue, bValue;
      if (key.includes(".")) {
        const keys = key.split(".");
        aValue = keys.reduce((acc, curr) => (acc ? acc[curr] : undefined), a);
        bValue = keys.reduce((acc, curr) => (acc ? acc[curr] : undefined), b);
      } else {
        aValue = a[key];
        bValue = b[key];
      }
      if (typeof aValue === "string") {
        aValue = aValue.trim().toLowerCase();
      }
      if (typeof bValue === "string") {
        bValue = bValue.trim().toLowerCase();
      }
      if (!isNaN(aValue) && !isNaN(bValue)) {
        aValue = Number(aValue);
        bValue = Number(bValue);
      }
      if (typeof aValue === "string" && typeof bValue === "string") {
        return direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      if (typeof aValue === "number" && typeof bValue === "number") {
        return direction === "asc" ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });

    setTableData(sortedData);
  };

  const applySorting = (data: any[]) => {
    if (!sortBy) return data;

    return [...data].sort((a, b) => {
      let valueA, valueB;

      if (
        a.resources &&
        a.resources[0] &&
        a.resources[0][sortBy] !== undefined
      ) {
        valueA = a.resources[0][sortBy];
        valueB = b.resources[0][sortBy];
      } else {
        valueA = a[sortBy];
        valueB = b[sortBy];
      }

      if (valueA < valueB) return sortOrder === "Ascending" ? -1 : 1;
      if (valueA > valueB) return sortOrder === "Ascending" ? 1 : -1;
      return 0;
    });
  };

  const applyGrouping = (data: any[]) => {
    if (!groupBy) return [{ group: null, items: data }];
    const grouped = data.reduce((acc: any, item: any) => {
      const groupValue = item[groupBy];
      if (!acc[groupValue]) acc[groupValue] = [];
      acc[groupValue].push(item);
      return acc;
    }, {});
    return Object.keys(grouped).map((key) => ({
      group: key,
      items: grouped[key],
    }));
  };

  const filteredData = tableData.filter((asset) => {
    const matchesSearchQuery = Object.values(asset).some(
      (value) =>
        value != null &&
        value.toString().toLowerCase().includes(searchQuery.toLowerCase())
    );
    const matchesAssetType = selectedAssetType
      ? asset.asset_type === selectedAssetType
      : true;
    const matchesISO = selectedISO ? asset.ISO === selectedISO : true;
    return matchesSearchQuery && matchesAssetType && matchesISO;
  });

  useEffect(() => {
    setNoResults(filteredData.length === 0);
  }, [filteredData]);

  const sortedData = applySorting(filteredData);
  const groupedData = applyGrouping(sortedData);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <LoadingSpinner />
      </div>
    );
  }
  const toggleRowSelection = (row: any) => {
    setSelectedRows((prev) => {
      if (prev.includes(row)) {
        // remove from selection
        return prev.filter((r) => r !== row);
      } else {
        // add to selection
        return [...prev, row];
      }
    });
  };
  const copySelectedRows = () => {
    const rowsToCopy = selectedRows.length > 0 ? selectedRows : tableData;

    if (rowsToCopy.length === 0) {
      return;
    }

    const headers = [
      "Type",
      "Name",
      "ISO",
      "RT_VOL",
      "DA_VOL",
      "RT_LMP",
      "DA_LMP",
    ];

    const rowsText = rowsToCopy
      .map((row) =>
        [
          row.asset_type || "-",
          row.asset_name,
          row.ISO,
          row.RT_VOL,
          row.DA_VOL,
          Number(row.RT_LMP).toFixed(2),
          Number(row.DA_LMP).toFixed(2),
        ].join("\t")
      )
      .join("\n");

    const copyText = headers.join("\t") + "\n" + rowsText;

    navigator.clipboard.writeText(copyText);
  };

  const formatCurrency = (val: any) => {
    const num = Number(val);
    if (isNaN(num)) return "-"; // handles "-", null, undefined, etc.
    return num < 0 ? `-$${Math.abs(num).toFixed(2)}` : `$${num.toFixed(2)}`;
  };

  const isValidValue = (val: string | number | null | undefined): boolean => {
    if (val === null || val === undefined) return false;
    const num = Number(val);
    return !isNaN(num) && num !== 0;
  };
  const formatCompact = (val: string | number): string => {
    const num = Number(val);
    if (isNaN(num)) return "";

    if (num >= 1_000_000_000)
      return (num / 1_000_000_000).toFixed(2).replace(/\.?0+$/, "") + "B";
    if (num >= 1_000_000)
      return (num / 1_000_000).toFixed(2).replace(/\.?0+$/, "") + "M";
    if (num >= 1_000)
      return (num / 1_000).toFixed(2).replace(/\.?0+$/, "") + "K";
    return num.toFixed(2).replace(/\.?0+$/, "");
  };

  return (
    <div className="bg-white border border-gray-300 rounded-lg shadow-lg mx-5 sm:mx-5 md:mx-10 lg:mx-20 my-5 max-w-full overflow-visible">
      <div className="flex justify-end items-center mb-2">
        <div className="flex space-x-4 mt-5 mx-4">
          <FaRegFilePdf
            className="text-gray-500 hover:text-red-500 text-xl transform hover:scale-110 transition"
            title="Export as PDF"
            onClick={() => {
              exportTableToPDF(
                "table-container",
                "Portfolio Table",
                "Portfolio Landing",
                tableData,
                logo
              );
            }}
          />
          <BsFiletypeCsv
            className="text-gray-500 hover:text-red-500 text-xl transform hover:scale-110 transition"
            title="Export as CSV"
            onClick={() => exportToCSV(tableData, "exported_data")}
          />
          <TbFileExcel
            className="text-gray-500 hover:text-red-500 text-xl transform hover:scale-110 transition"
            title="Export as Excel"
            onClick={() =>
            exportTableOldToExcel(
                "table-container",
  "Portfolio Table",
  "Portfolio Landing",
  tableData,
  logo,
                []
)
            }
          />
          <FaRegCopy
            className="text-gray-500 hover:text-red-500 text-xl transform hover:scale-110 transition"
            title="Copy Data"
            onClick={copySelectedRows}
          />
        </div>
      </div>

      <div className="overflow-x-auto max-h-[750px] mb-2 relative custom-scrollbar overflow-visible">
        <div id="table-container">
          <table className="min-w-full border-collapse text-sm font-poppin">
            <thead className="sticky top-0  bg-[#e3e5f2] z-10">
              <tr className="border-b border-gray-300">
                <th className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-700">
                  <div className="flex items-center space-x-2">
                    <span>Type</span>
                  </div>
                </th>
                <th
                  className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 cursor-pointer"
                  onClick={() => sortData("asset_name")}
                >
                  <div className="flex items-center space-x-2">
                    <span>Name</span>
                    <IoMdArrowRoundDown
                      className={`text-gray-500 text-lg ${
                        sortConfig.key === "asset_name"
                          ? sortConfig.direction === "asc"
                            ? "rotate-180"
                            : ""
                          : ""
                      }`}
                    />
                  </div>
                </th>
                <th
                  className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 cursor-pointer"
                  onClick={() => sortData("ISO")}
                >
                  <div className="flex items-center space-x-2">
                    <span>ISO</span>
                    <IoMdArrowRoundDown
                      className={`text-gray-500 text-lg ${
                        sortConfig.key === "ISO"
                          ? sortConfig.direction === "asc"
                            ? "rotate-180"
                            : ""
                          : ""
                      }`}
                    />
                  </div>
                </th>
                <th
                  className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 cursor-pointer"
                  onClick={() => sortData("RT_VOL")}
                >
                  <div className="flex items-center space-x-2">
                    <GiElectric className="text-purple-700 text-2xl" />
                    <span>RT.VOL</span>
                    <IoMdArrowRoundDown
                      className={`text-gray-500 text-lg ${
                        sortConfig.key === "RT_VOL"
                          ? sortConfig.direction === "asc"
                            ? "rotate-180"
                            : ""
                          : ""
                      }`}
                    />
                  </div>
                </th>
                <th
                  className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 cursor-pointer"
                  onClick={() => sortData("DA_VOL")}
                >
                  <div className="flex items-center space-x-2">
                    <GiElectric className="text-gray-700 text-2xl" />
                    <span>DA.VOL</span>
                    <IoMdArrowRoundDown
                      className={`text-gray-500 text-lg ${
                        sortConfig.key === "DA_VOL"
                          ? sortConfig.direction === "asc"
                            ? "rotate-180"
                            : ""
                          : ""
                      }`}
                    />
                  </div>
                </th>
                <th
                  className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 cursor-pointer"
                  onClick={() => sortData("RT_LMP")}
                >
                  <div className="flex items-center space-x-2">
                    <BiSolidDollarCircle className="text-custom-green text-2xl" />
                    <span>RT.LMP</span>
                    <IoMdArrowRoundDown
                      className={`text-gray-500 text-lg ${
                        sortConfig.key === "RT_LMP"
                          ? sortConfig.direction === "asc"
                            ? "rotate-180"
                            : ""
                          : ""
                      }`}
                    />
                  </div>
                </th>
                <th
                  className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 cursor-pointer"
                  onClick={() => sortData("DA_LMP")}
                >
                  <div className="flex items-center space-x-2">
                    <BiSolidDollarCircle className="text-gray-500 text-2xl" />
                    <span>DA.LMP</span>
                    <IoMdArrowRoundDown
                      className={`text-gray-500 text-lg ${
                        sortConfig.key === "DA_LMP"
                          ? sortConfig.direction === "asc"
                            ? "rotate-180"
                            : ""
                          : ""
                      }`}
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr>
                  <td
                    colSpan={7}
                    className="py-4 text-center text-red-600 font-semibold "
                  >
                    {error}
                  </td>
                </tr>
              ) : noResults ? (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center text-gray-600 font-semibold p-4"
                  >
                    No matching results found. Try adjusting your filters.
                  </td>
                </tr>
              ) : (
                groupedData.map(({ group, items }: any) => (
                  <React.Fragment key={group || "default"}>
                    {group && (
                      <tr className="bg-purple-200">
                        <td colSpan={7} className="py-2 px-4 font-bold">
                          {group}
                        </td>
                      </tr>
                    )}
                    {items.map((asset: any, index: number) => (
                      <tr
                        key={index}
                        className={`${
                          index % 2 === 0 ? "bg-white-200" : "bg-gray-100"
                        } font-poppins cursor-pointer ${
                          selectedRows.includes(asset) ? "bg-gray-200" : ""
                        } ${
                          !hasAccess("/asset-dashboard")
                            ? "cursor-not-allowed"
                            : ""
                        }`}
                        onClick={() => toggleRowSelection(asset)}
                        onDoubleClick={() => handleCardClick(asset)}
                        title={
                          !hasAccess("/asset-dashboard")
                            ? "Access Restricted"
                            : ""
                        }
                      >
                        <td className="py-3 px-2 sm:px-4 text-center">
                          <img
                            src={getTypeIcon(asset.asset_type) || ""}
                            alt={asset.asset_type || "N/A"}
                            className="w-8 h-8"
                          />
                        </td>
                        <td className="py-3 px-2 sm:px-4 text-gray-700">
                          {asset.asset_name}
                        </td>
                        <td className="py-3 px-2 sm:px-4 truncate ">
                          <div className="flex items-center">
                            <span className="font-semibold text-gray-700 mr-4">
                              {asset.ISO}
                            </span>
                            <div className="flex items-center gap-2">
                              {/* Battery */}
                              {asset.asset_type?.toLowerCase() === "battery" && isValidValue(asset.batteryLevel) && 
                                (
                                  <BatteryLevel
                                    currentEnergy={Number(asset.batteryLevel)}
                                    maxEnergy={Number(asset.maxEnergy)}
                                    showTooltip={false}
                                  />
                                )}

                              {/* Wind Speed */}
                              {asset.asset_type?.toLowerCase() === "wind" && isValidValue(asset.windspeed) &&
                                (
                                  <span className="text-xs text-gray-600 flex items-center">
                                    <FaWind className="mr-1" />
                                    {formatCompact(asset.windspeed)} knots
                                  </span>
                                )}

                              {/* Gas Burn */}
                              {asset.asset_type?.toLowerCase() === "gas" && isValidValue(asset.gasBurnValue) && 
                                (
                                  <span className="text-xs text-gray-600 flex items-center">
                                    <ImFire className="mr-1" />
                                    Gas Burn:{" "}
                                    {formatCompact(asset.gasBurnValue)}
                                  </span>
                                )}

                              {/* Wind / Solar Forecasts */}
                              {(["wind", "solar"].includes(asset.asset_type?.toLowerCase())) &&
                                asset.Day1_Forecast !== null &&
                                asset.Day1_Forecast !== undefined &&
                                asset.Day2_Forecast !== null &&
                                asset.Day2_Forecast !== undefined && (
                                  <span className="text-xs text-gray-600 flex items-center">
                                    <LuFlagTriangleRight className="ml-1 text-[10px]" />
                                    Day 1 Forecast: {asset.Day1_Forecast} MW
                                    <LuFlagTriangleRight className="ml-1 text-[10px]" />
                                    Day 2 Forecast: {asset.Day2_Forecast} MW
                                  </span>
                                )}
                            </div>
                          </div>
                        </td>
                        <td
                          className={`py-3 px-2 sm:px-4 text-right ${
                            asset.RT_VOL < 0
                              ? "text-custom-red"
                              : "text-gray-700"
                          }`}
                        >
                          {asset.RT_VOL}
                        </td>
                        <td
                          className={`py-3 px-2 sm:px-4 text-right ${
                            asset.DA_VOL < 0
                              ? "text-custom-red"
                              : "text-gray-700"
                          }`}
                        >
                          {asset.DA_VOL}
                        </td>
                        <td
                          className={`py-3 px-2 sm:px-4 text-right ${
                            Number(asset.RT_LMP) < 0
                              ? "text-custom-red"
                              : "text-gray-700"
                          }`}
                        >
                          {formatCurrency(asset.RT_LMP)}
                        </td>

                        <td
                          className={`py-3 px-2 sm:px-4 text-right ${
                            Number(asset.DA_LMP) < 0
                              ? "text-custom-red"
                              : "text-gray-700"
                          }`}
                        >
                          {formatCurrency(asset.DA_LMP)}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TableCard;
